import React from "react";
import { Tv, SkipForward, ExternalLink, User } from "lucide-react";
import { Modal } from "../common/Modal";
import { YouTubePlayer } from "./YouTubePlayer";
import { usePartyStore } from "../../stores/partyStore";
import { useIsMobile } from "../../hooks/useIsMobile";
import { removeSongFromQueue } from "../../lib/firebase";
import type { QueueSong } from "../../types";

interface PlayerModalProps {
  isOpen: boolean;
  onClose: () => void;
  song: QueueSong | null;
}

export const PlayerModal: React.FC<PlayerModalProps> = ({
  isOpen,
  onClose,
  song,
}) => {
  const { currentParty, userRole, setTVMode } = usePartyStore();
  const isMobile = useIsMobile();
  const isHost = userRole === "HOST";

  const handleNextSong = async () => {
    if (!currentParty || !song) return;
    const songIdToDelete = song.id;

    try {
      // Optimistic local state update for instant transition
      const { queue, setQueue, setCurrentSong } = usePartyStore.getState();
      const nextQueue = queue.filter((q) => q.id !== songIdToDelete);
      setQueue(nextQueue);
      setCurrentSong(nextQueue.length > 0 ? nextQueue[0] : null);

      await removeSongFromQueue(currentParty.id, songIdToDelete);
    } catch (err: any) {
      console.error("Error advancing song:", err);
    }
  };

  const handleSwitchToTV = () => {
    onClose();
    setTVMode(true);
  };

  if (!song) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={song.title}
      maxWidth="max-w-3xl"
    >
      <div className="space-y-4">
        {/* Embedded YouTube Player */}
        <div className="rounded-2xl overflow-hidden shadow-2xl">
          <YouTubePlayer
            currentSong={song}
            onSongEnd={handleNextSong}
            isHost={isHost}
          />
        </div>

        {/* Singer Info & Action Controls */}
        <div className="bg-background/60 p-4 rounded-2xl border border-border flex flex-wrap items-center justify-between gap-3">
          <div className="space-y-0.5 min-w-0 flex-1">
            <h4 className="text-sm font-bold text-text-main truncate">
              {song.title}
            </h4>
            <div className="flex items-center gap-2 text-xs text-text-muted">
              <span>{song.channelTitle}</span>
              <span>•</span>
              <span className="flex items-center gap-1 text-accent">
                <User className="w-3 h-3" />
                <span>Added by {song.addedByUserName}</span>
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {/* Open in YouTube Tab */}
            <a
              href={`https://www.youtube.com/watch?v=${song.videoId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="py-2.5 px-3.5 rounded-xl bg-card border border-border hover:border-text-muted text-text-muted hover:text-text-main text-xs font-semibold flex items-center gap-1.5 transition-colors"
              title="Open video in new YouTube tab"
            >
              <ExternalLink className="w-4 h-4" />
              <span className="hidden sm:inline">YouTube</span>
            </a>

            {/* Switch to TV Display Mode (Desktop / Tablet only) */}
            {!isMobile && (
              <button
                onClick={handleSwitchToTV}
                className="py-2.5 px-3.5 rounded-xl bg-accent/10 border border-accent/30 hover:border-accent text-accent text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer"
                title="Open full TV Screen display mode"
              >
                <Tv className="w-4 h-4" />
                <span className="hidden sm:inline">TV Mode</span>
              </button>
            )}

            {/* Skip / Next Song (Host only) */}
            {isHost && (
              <button
                onClick={handleNextSong}
                className="py-2.5 px-4 rounded-2xl bg-primary hover:bg-primary-hover text-white text-xs font-bold flex items-center gap-1.5 transition-all shadow-md shadow-primary/20 cursor-pointer"
                title="Finish and advance to next song in queue"
              >
                <SkipForward className="w-4 h-4" />
                <span>Next Song</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
